import { useEffect, useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { McpIcon } from '../components/icons';
import { api } from '../api';
import { PortalAccessRequestOut as PortalAccessRequest, PlatformRequestPortalOut as PlatformRequestResult, WorkflowGenerationRequestOut } from '../types/apiRequests';
import { useBeheerder } from '../hooks/useBeheerder';
import { TIER_LABEL_KEYS, REQUEST_STATUS_CONFIG } from '../constants';
import { useTranslation } from '../i18n/useTranslation';
import type { TranslationKey } from '../i18n';

function StatusBadge({ status }: { status: string }) {
  const t = useTranslation();
  const c = REQUEST_STATUS_CONFIG[status] || REQUEST_STATUS_CONFIG.pending;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: '6px',
        bgcolor: c.bgColor,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.dotColor }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: c.textColor }}>
        {t(c.labelKey)}
      </Typography>
    </Box>
  );
}

export default function MyRequestsPage() {
  const { getAccessTokenSilently } = useOidcContext();
  const t = useTranslation();
  const isBeheerder = useBeheerder();
  const [requests, setRequests] = useState<PortalAccessRequest[]>([]);
  const [platformRequests, setPlatformRequests] = useState<PlatformRequestResult[]>([]);
  const [aiRequests, setAiRequests] = useState<WorkflowGenerationRequestOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [pendingCancelType, setPendingCancelType] = useState<'access' | 'platform' | null>(null);

  const loadRequests = useCallback(async () => {
    try {
      setError(null);
      const token = await getAccessTokenSilently();
      const [access, platform, ai] = await Promise.all([
        api.getMyRequests(token),
        isBeheerder ? api.getMyPlatformRequests(token) : Promise.resolve([]),
        isBeheerder ? api.getMyWorkflowGenerationRequests(token) : Promise.resolve([]),
      ]);
      setRequests(access);
      setPlatformRequests(platform);
      setAiRequests(ai);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isBeheerder, t]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const openCancelConfirm = useCallback((id: string, type: 'access' | 'platform') => {
    setPendingCancelId(id);
    setPendingCancelType(type);
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!pendingCancelId || !pendingCancelType) return;
    setConfirmDialogOpen(false);
    setCancellingId(pendingCancelId);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      if (pendingCancelType === 'access') {
        const updated = await api.cancelAccessRequest(token, pendingCancelId);
        setRequests((prev) => prev.map((r) => (r.id === pendingCancelId ? updated : r)));
      } else {
        const updated = await api.cancelPlatformRequest(token, pendingCancelId);
        setPlatformRequests((prev) => prev.map((r) => (r.id === pendingCancelId ? updated : r)));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setCancellingId(null);
      setPendingCancelId(null);
      setPendingCancelType(null);
    }
  }, [pendingCancelId, pendingCancelType, getAccessTokenSilently, t]);

  const handleDismissCancel = useCallback(() => {
    setConfirmDialogOpen(false);
    setPendingCancelId(null);
    setPendingCancelType(null);
  }, []);

  const isEmpty =
    !loading
    && platformRequests.length === 0
    && requests.length === 0
    && aiRequests.length === 0;

  return (
    <>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {t('myRequests.page.title')}
        </Typography>
        <Typography variant="subtitle1">
          {t('myRequests.page.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card sx={{ p: 4 }}>
          <Skeleton variant="rounded" height={200} />
        </Card>
      ) : isEmpty ? (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <McpIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
            {t('myRequests.empty.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {t('myRequests.empty.subtitle')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {aiRequests.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AutoAwesomeIcon sx={{ fontSize: 16, color: '#6366F1' }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                  {t('myRequests.aiRequests.sectionTitle')}
                </Typography>
              </Box>
              <Card sx={{ overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.aiRequests.col.prompt')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.note')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aiRequests.map((req) => (
                      <TableRow key={req.id} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Tooltip title={req.prompt} placement="top-start">
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {req.prompt}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={req.status} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {new Date(req.created).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {req.review_note ? (
                            <Tooltip title={req.review_note}>
                              <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  color: 'text.secondary',
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {req.review_note}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '0.8rem', color: '#d4d4d8' }}>&mdash;</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}

          {platformRequests.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RocketLaunchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                  {t('myRequests.platformRequests.sectionTitle')}
                </Typography>
              </Box>
              <Card sx={{ overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.platformRequests.col.tier')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.note')}</TableCell>
                      <TableCell sx={{ width: 60 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {platformRequests.map((req) => (
                      <TableRow key={req.id} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {TIER_LABEL_KEYS[req.requested_tier]
                              ? t(TIER_LABEL_KEYS[req.requested_tier] as TranslationKey)
                              : req.requested_tier}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={req.status} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {new Date(req.created).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {req.review_note ? (
                            <Tooltip title={req.review_note}>
                              <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  color: 'text.secondary',
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {req.review_note}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '0.8rem', color: '#d4d4d8' }}>&mdash;</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {req.status === 'pending' && (
                            <Tooltip title={t('myRequests.cancel')}>
                              <IconButton
                                size="small"
                                disabled={cancellingId === req.id}
                                onClick={() => openCancelConfirm(req.id, 'platform')}
                                sx={{ color: 'text.disabled', '&:hover': { color: '#C24242' } }}
                              >
                                <CloseIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}

          {requests.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <McpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                  {t('myRequests.accessRequests.sectionTitle')}
                </Typography>
              </Box>
              <Card sx={{ overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.accessRequests.col.server')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.accessRequests.col.type')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{t('myRequests.col.note')}</TableCell>
                      <TableCell sx={{ width: 60 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <McpIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                            <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                              {req.server_display_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {req.request_type === 'organization' ? t('myRequests.requestType.organization') : t('myRequests.requestType.user')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={req.status} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {new Date(req.created).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {req.review_note ? (
                            <Tooltip title={req.review_note}>
                              <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  color: 'text.secondary',
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {req.review_note}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '0.8rem', color: '#d4d4d8' }}>&mdash;</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {req.status === 'pending' && (
                            <Tooltip title={t('myRequests.cancel')}>
                              <IconButton
                                size="small"
                                disabled={cancellingId === req.id}
                                onClick={() => openCancelConfirm(req.id, 'access')}
                                sx={{ color: 'text.disabled', '&:hover': { color: '#C24242' } }}
                              >
                                <CloseIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          )}
        </Box>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleDismissCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('myRequests.cancel')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('myRequests.cancelConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismissCancel} color="inherit">
            {t('common.no')}
          </Button>
          <Button onClick={() => void handleConfirmCancel()} color="error" variant="contained">
            {t('common.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
